import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Users, CheckCircle, XCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useInviteDetails, useAcceptInvite, useDeclineInvite } from '../../api/hooks/useTeams';
import { useAuth } from '../../contexts/AuthContext';
import { TextInput } from '../../components/inputs/TextInput';
import { Button } from '../../components/actions/Button';
import { Alert } from '../../components/feedback/Alert';

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, refreshUser } = useAuth();

  const { data: invite, isLoading, error } = useInviteDetails(token);
  const acceptInvite = useAcceptInvite();
  const declineInvite = useDeclineInvite();

  const handleAccept = async () => {
    if (!token) return;
    try {
      await acceptInvite.mutateAsync(token);
      await refreshUser();
      navigate('/app/home');
    } catch (err) {
      // Error handled by mutation
    }
  };

  const handleDecline = async () => {
    if (!token) return;
    try {
      await declineInvite.mutateAsync(token);
      navigate('/app/home');
    } catch (err) {
      // Error handled by mutation
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Error state (expired, invalid, or already accepted)
  if (error || !invite) {
    const errorMessage = (error as any)?.response?.data?.message || 'This invite is no longer valid';
    const isExpired = errorMessage.includes('expired');
    const isAccepted = errorMessage.includes('accepted');

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-background-card rounded-xl border border-border p-8 text-center"
        >
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isAccepted ? 'bg-success/10' : 'bg-error/10'}`}>
            {isExpired ? (
              <AlertCircle className="w-8 h-8 text-error" />
            ) : isAccepted ? (
              <CheckCircle className="w-8 h-8 text-success" />
            ) : (
              <XCircle className="w-8 h-8 text-error" />
            )}
          </div>
          <h1 className="text-xl font-semibold text-text-primary mb-2">
            {isExpired ? 'Invite Expired' : isAccepted ? 'Already Accepted' : 'Invalid Invite'}
          </h1>
          <p className="text-text-secondary mb-6">
            {errorMessage}
          </p>
          <Button onClick={() => navigate('/app/home')}>
            Go Home
          </Button>
        </motion.div>
      </div>
    );
  }

  // Not authenticated - show registration form
  if (!isAuthenticated) {
    return <RegisterAndAcceptForm invite={invite} token={token!} onAccepted={() => navigate('/app/home')} />;
  }

  // Email mismatch warning
  const emailMismatch = user?.email?.toLowerCase() !== invite.email.toLowerCase();

  // Authenticated - show accept/decline
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-background-card rounded-xl border border-border p-8"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-xl font-semibold text-text-primary mb-2">
            Join {invite.team.name}
          </h1>
          <p className="text-text-secondary">
            {invite.invited_by.name} invited you to join as a{' '}
            <span className="font-medium capitalize">{invite.role}</span>
          </p>
        </div>

        {emailMismatch && (
          <div className="mb-6">
            <Alert variant="warning" title="Email mismatch">
              This invite was sent to {invite.email}, but you're signed in as {user?.email}.
            </Alert>
          </div>
        )}

        {acceptInvite.error && (
          <div className="mb-6">
            <Alert variant="error">
              {(acceptInvite.error as any)?.response?.data?.message || 'Failed to accept invite'}
            </Alert>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleDecline}
            disabled={declineInvite.isPending || acceptInvite.isPending}
            className="flex-1 px-4 py-2.5 border border-border rounded-lg text-text-secondary hover:bg-background-elevated disabled:opacity-50 transition-colors"
          >
            {declineInvite.isPending ? (
              <Loader2 size={18} className="animate-spin mx-auto" />
            ) : (
              'Decline'
            )}
          </button>
          <button
            onClick={handleAccept}
            disabled={acceptInvite.isPending || declineInvite.isPending || emailMismatch}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-text-inverse rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
          >
            {acceptInvite.isPending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Joining...
              </>
            ) : (
              'Accept & Join'
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// Registration form component for unauthenticated users
function RegisterAndAcceptForm({ invite, token, onAccepted }: { invite: any; token: string; onAccepted: () => void }) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [error, setError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const { register, refreshUser } = useAuth();
  const acceptInvite = useAcceptInvite();
  const navigate = useNavigate();

  const handleRegisterAndAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsRegistering(true);

    try {
      // Register the user - this synchronously sets auth headers
      // Pass invite_token so backend skips personal team creation + verification email
      await register({ name, email: invite.email, password, password_confirmation: confirmPassword, invite_token: token });

      // Accept the invite (now authenticated)
      await acceptInvite.mutateAsync(token);

      // Refresh user to get updated team data
      await refreshUser();

      // Navigate to home
      onAccepted();
    } catch (err: any) {
      console.error('Registration error:', err?.response?.data);

      // Extract validation errors
      const errors = err?.response?.data?.errors;
      const message = err?.response?.data?.message;

      if (errors && typeof errors === 'object') {
        // Combine all error messages
        const errorMessages = Object.values(errors)
          .flat()
          .filter(msg => typeof msg === 'string')
          .join(' ');
        setError(errorMessages || message || 'Failed to create account. Please check your password requirements.');
      } else if (message) {
        setError(message);
      } else {
        setError('Failed to create account. Password must be 8+ characters with uppercase, lowercase, and a number.');
      }
    } finally {
      setIsRegistering(false);
    }
  };

  if (showLogin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-background-card rounded-xl border border-border p-8"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-xl font-semibold text-text-primary mb-2">
              Join {invite.team.name}
            </h1>
            <p className="text-text-secondary">
              {invite.invited_by.name} invited you to join as a{' '}
              <span className="font-medium capitalize">{invite.role}</span>
            </p>
          </div>

          <div className="p-4 bg-background-elevated rounded-lg mb-6">
            <p className="text-sm text-text-secondary text-center">
              Already have an account? Sign in to accept this invitation
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => navigate(`/login?redirect=/invites/${token}`)}
              className="w-full px-4 py-2.5 bg-primary text-text-inverse rounded-lg hover:bg-primary-hover transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={() => setShowLogin(false)}
              className="w-full px-4 py-2.5 border border-border rounded-lg text-text-secondary hover:bg-background-elevated transition-colors"
            >
              Back to Registration
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-background-card rounded-xl border border-border p-8"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-xl font-semibold text-text-primary mb-2">
            You've been invited to join {invite.team.name}
          </h1>
          <p className="text-text-secondary">
            {invite.invited_by.name} invited you as a{' '}
            <span className="font-medium capitalize">{invite.role}</span>
          </p>
        </div>

        <form onSubmit={handleRegisterAndAccept} className="space-y-4">
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm text-text-secondary font-medium mb-1">Create Your Account</p>
            <p className="text-xs text-text-secondary">Enter your details to accept the invitation</p>
          </div>

          <TextInput
            label="Email"
            type="email"
            value={invite.email}
            disabled
            className="bg-background-elevated"
          />

          <TextInput
            label="Name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your full name"
            required
            autoFocus
          />

          <div className="relative">
            <TextInput
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[38px] text-text-secondary hover:text-text-primary"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div className="relative">
            <TextInput
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              error={password !== confirmPassword && confirmPassword ? 'Passwords do not match' : undefined}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-[38px] text-text-secondary hover:text-text-primary"
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {error && (
            <Alert variant="error">{error}</Alert>
          )}

          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={isRegistering || acceptInvite.isPending || password !== confirmPassword}
          >
            {isRegistering || acceptInvite.isPending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {isRegistering ? 'Creating account...' : 'Joining team...'}
              </>
            ) : (
              'Accept Invitation & Create Account'
            )}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowLogin(true)}
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Already have an account? <span className="font-medium">Sign in</span>
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
